/**
 * Fond de l'accueil du back-office : modèle de la préférence utilisateur
 * (`dashboard.background`), préréglages proposés et lecture tolérante.
 * Le calcul des contrastes est dans `contrast.ts` (pur) et
 * `background-contrast.ts` (mesure du fond affiché, DOM).
 */
import {
  type BackgroundContrast,
  contrastForColor,
  isStoredContrast,
} from "./contrast";

export const DASHBOARD_BACKGROUND_PREF_KEY = "dashboard.background";

export type GradientPreset = {
  id: string;
  labelKey: string;
  label: string;
  /** Angle CSS (0deg = vers le haut, sens horaire). */
  angle: number;
  stops: { color: string; at: number }[];
};

/**
 * Six images au choix : trois fournies avec l'application et trois personnelles
 * (une par emplacement, stockées côté API).
 */
export const PRESET_IMAGE_IDS = ["preset-1", "preset-2", "preset-3"] as const;
export const USER_IMAGE_SLOTS = [1, 2, 3] as const;
export type PresetImageId = (typeof PRESET_IMAGE_IDS)[number];
export type UserImageSlot = (typeof USER_IMAGE_SLOTS)[number];
export type UserImageId = `user-${UserImageSlot}`;
export type BackgroundImageId = PresetImageId | UserImageId;

export const USER_IMAGE_IDS: UserImageId[] = USER_IMAGE_SLOTS.map(
  (slot) => `user-${slot}` as const,
);
/** Les fonds enregistrés avant les six images n'ont pas d'identifiant : c'est la première image personnelle. */
export const LEGACY_IMAGE_ID: UserImageId = "user-1";

export type DashboardBackground =
  | { type: "none" }
  | { type: "color"; color: string }
  | { type: "gradient"; gradientId: string }
  /** Le fichier d'une image personnelle est côté API ; on garde ici la mesure faite au choix. */
  | { type: "image"; imageId: BackgroundImageId; contrast: BackgroundContrast };

export function isPresetImageId(id: BackgroundImageId): id is PresetImageId {
  return (PRESET_IMAGE_IDS as readonly string[]).includes(id);
}

export function userSlotOf(id: UserImageId): UserImageSlot {
  return Number(id.slice("user-".length)) as UserImageSlot;
}

function parseImageId(value: unknown): BackgroundImageId | null {
  if (value === undefined) return LEGACY_IMAGE_ID;
  const known: readonly unknown[] = [...PRESET_IMAGE_IDS, ...USER_IMAGE_IDS];
  return known.includes(value) ? (value as BackgroundImageId) : null;
}

/** Image fournie avec l'application (`public/home-backgrounds/`). */
export function presetImageUrl(id: PresetImageId): string {
  return `/home-backgrounds/${id}.webp`;
}

/** URL de l'image choisie : fichier fourni, ou URL signée de l'emplacement personnel. */
export function imageUrlFor(
  id: BackgroundImageId,
  userUrls: readonly (string | null)[],
): string | null {
  return isPresetImageId(id)
    ? presetImageUrl(id)
    : (userUrls[userSlotOf(id) - 1] ?? null);
}

export const DEFAULT_BACKGROUND: DashboardBackground = { type: "none" };

/** Fond « Aucun fond » : le gris neutre du back-office. */
export const NEUTRAL_BACKGROUND_COLOR = "#f2f2f2";

export const DEFAULT_SOLID_COLOR = "#1f2a44";

export const SOLID_COLOR_SWATCHES = [
  "#f2f2f2",
  "#ffffff",
  "#e8eef7",
  "#f4ece1",
  "#1f2a44",
  "#0f3d3e",
  "#4a2c40",
  "#07070a",
];

export const GRADIENT_PRESETS: GradientPreset[] = [
  {
    id: "aurore",
    labelKey: "bo.home.background.gradient.aurore",
    label: "Aurore",
    angle: 160,
    stops: [
      { color: "#4f86e8", at: 0 },
      { color: "#9a82ef", at: 45 },
      { color: "#e3b693", at: 80 },
      { color: "#f2a463", at: 100 },
    ],
  },
  {
    id: "ocean",
    labelKey: "bo.home.background.gradient.ocean",
    label: "Océan",
    angle: 150,
    stops: [
      { color: "#0b3c5d", at: 0 },
      { color: "#1984d3", at: 55 },
      { color: "#6fd3e0", at: 100 },
    ],
  },
  {
    id: "foret",
    labelKey: "bo.home.background.gradient.foret",
    label: "Forêt",
    angle: 160,
    stops: [
      { color: "#0f3d3e", at: 0 },
      { color: "#2f6b4f", at: 50 },
      { color: "#b9c98a", at: 100 },
    ],
  },
  {
    id: "crepuscule",
    labelKey: "bo.home.background.gradient.crepuscule",
    label: "Crépuscule",
    angle: 165,
    stops: [
      { color: "#1b1340", at: 0 },
      { color: "#7a2e6b", at: 55 },
      { color: "#f07b5a", at: 100 },
    ],
  },
  {
    id: "brume",
    labelKey: "bo.home.background.gradient.brume",
    label: "Brume",
    angle: 135,
    stops: [
      { color: "#fdfbfb", at: 0 },
      { color: "#dfe7f2", at: 100 },
    ],
  },
  {
    id: "peche",
    labelKey: "bo.home.background.gradient.peche",
    label: "Pêche",
    angle: 135,
    stops: [
      { color: "#ffecd2", at: 0 },
      { color: "#fcb69f", at: 100 },
    ],
  },
  {
    id: "ocean-clair",
    labelKey: "bo.home.background.gradient.oceanClair",
    label: "Océan clair",
    angle: 150,
    stops: [
      { color: "#2b7fb8", at: 0 },
      { color: "#5cb8ee", at: 55 },
      { color: "#b4eef4", at: 100 },
    ],
  },
  {
    id: "foret-clair",
    labelKey: "bo.home.background.gradient.foretClair",
    label: "Forêt claire",
    angle: 160,
    stops: [
      { color: "#2f7d6d", at: 0 },
      { color: "#6fb98d", at: 50 },
      { color: "#e2ebbd", at: 100 },
    ],
  },
  {
    id: "anthracite",
    labelKey: "bo.home.background.gradient.anthracite",
    label: "Anthracite",
    angle: 160,
    stops: [
      { color: "#40444b", at: 0 },
      { color: "#08090b", at: 100 },
    ],
  },
  // Les trois suivants reprennent les maquettes de Christian (carte #1079).
  // Pastèque est un dégradé « maillé » à quatre coins sur la maquette ; on le
  // rend ici en dégradé linéaire, seul modèle que mesure le calcul de contraste.
  {
    id: "pasteque",
    labelKey: "bo.home.background.gradient.pasteque",
    label: "Pastèque",
    angle: 115,
    stops: [
      { color: "#b84560", at: 0 },
      { color: "#ee7b84", at: 30 },
      { color: "#f1e58a", at: 62 },
      { color: "#8fb06c", at: 82 },
      { color: "#2f4a3d", at: 100 },
    ],
  },
  {
    id: "lagon",
    labelKey: "bo.home.background.gradient.lagon",
    label: "Lagon",
    angle: 135,
    stops: [
      { color: "#5ff3df", at: 0 },
      { color: "#b4d9f0", at: 55 },
      { color: "#f2ccfc", at: 100 },
    ],
  },
  {
    id: "neon",
    labelKey: "bo.home.background.gradient.neon",
    label: "Néon",
    angle: 90,
    stops: [
      { color: "#3a9fd8", at: 0 },
      { color: "#7a78dc", at: 50 },
      { color: "#b72be3", at: 100 },
    ],
  },
];

export function findGradientPreset(id: string): GradientPreset {
  return GRADIENT_PRESETS.find((p) => p.id === id) ?? GRADIENT_PRESETS[0];
}

export function gradientCss(preset: GradientPreset): string {
  const stops = preset.stops.map((s) => `${s.color} ${s.at}%`).join(", ");
  return `linear-gradient(${preset.angle}deg, ${stops})`;
}

/** Lit la préférence en tolérant une valeur absente ou mal formée. */
export function parseDashboardBackground(value: unknown): DashboardBackground {
  if (!value || typeof value !== "object") return DEFAULT_BACKGROUND;
  const v = value as Record<string, unknown>;
  if (v.type === "color" && typeof v.color === "string") {
    return { type: "color", color: v.color };
  }
  if (v.type === "gradient" && typeof v.gradientId === "string") {
    return { type: "gradient", gradientId: v.gradientId };
  }
  const imageId = parseImageId(v.imageId);
  if (v.type === "image" && imageId && isStoredContrast(v.contrast)) {
    return {
      type: "image",
      imageId,
      // Mesures enregistrées avant que le header ne suive le fond : verre sombre.
      contrast: { headerTone: "dark", ...v.contrast },
    };
  }
  return DEFAULT_BACKGROUND;
}

/** Contraste « Aucun fond » : blocs opaques comme sur la maquette « fond uni ». */
export function contrastForNeutral(): BackgroundContrast {
  return {
    ...contrastForColor(NEUTRAL_BACKGROUND_COLOR),
    tone: "light",
    scrim: 1,
  };
}

/**
 * Images acceptées en fond. Mêmes règles que l'API
 * (`inventozen-api/src/api/user-config/dashboard-background.ts`) : à garder
 * alignées, l'API reste juge en dernier ressort.
 */
export const BACKGROUND_IMAGE_RULES = {
  mimeTypes: ["image/jpeg", "image/png", "image/webp"],
  maxBytes: 15 * 1024 * 1024,
} as const;

export function isAcceptedBackgroundImage(file: File): boolean {
  return (
    (BACKGROUND_IMAGE_RULES.mimeTypes as readonly string[]).includes(
      file.type,
    ) && file.size <= BACKGROUND_IMAGE_RULES.maxBytes
  );
}
